import {Injectable} from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import {Store} from '@ngxs/store';
import {AuthState} from '@core/store/auth/auth.state';
import {GetNewToken, LoginFailed, Logout} from '@core/store/auth/auth.actions';
import { API } from '@configs/api.config';
import { SetSpinner } from '@core/store/shared.actions';
import { Observable, throwError, BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HttpInterceptorService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  private readonly MFA_BYPASS_PATHS = [
    '/users/login',
    '/users/verify-mfa-login',
    '/users/send-login-mfa-otp',
    '/users/complete-mfa-setup-login',
    '/mfa/init-setup',
    '/mfa/verify-enable',
    '/mfa/init-email-setup',
    '/mfa/verify-email-code',
    '/mfa/init-sms-setup',
    '/mfa/verify-sms-code',
    '/mfa/resend-otp',
  ];

  constructor(private store: Store, public router: Router) {
  }

  /**
   * @description - intercept the http request and put jwt token
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.getToken();
    const isMfaBypass = this.MFA_BYPASS_PATHS.some(path => req.url.includes(path));
    let clonedRequest = req;
    if (!req.url.includes('storage.googleapis.com') && !isMfaBypass) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      clonedRequest = req.clone({setHeaders: {Authorization: 'Bearer ' + token}});
    }
    return next.handle(clonedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (req.url.includes(API.auth.getToken) || req.url.includes(API.auth.refreshToken) || req.url.includes('/users/login')) {
          this.store.dispatch(new LoginFailed((error.error as { message?: string })?.message || error.message));
          return throwError(error);
        }
        if (isMfaBypass) {
          return throwError(error);
        }
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const rememberMe = currentUser?.rememberMe || false;
        if (error.status === 401) {
          return this.handleTokenExpiration(error, req, next, rememberMe);
        }
        this.store.dispatch(new SetSpinner(false));
        return throwError(error);
      })
    );
  }

    /**
     * @description - return token from storage or cookie
     */
    private getToken(): string {
      return this.store.selectSnapshot(AuthState.token);
    }

    private handleTokenExpiration(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler, rememberMe?: boolean):
       Observable<HttpEvent<any>> {
      console.log('handleTokenExpiration');
      if (!this.isRefreshing) {
        this.isRefreshing = true;
        this.refreshTokenSubject.next(null);

        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          return this.handleRefreshTokenError();
        }

        return this.store.dispatch(new GetNewToken(rememberMe)).pipe(
          switchMap(() => {
            const newToken = this.getToken();
            this.isRefreshing = false;
            this.refreshTokenSubject.next(newToken);
            return next.handle(this.addTokenToRequest(request, newToken));
          }),
          catchError(refreshError => this.handleRefreshTokenError(refreshError))
        );
      }

      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => next.handle(this.addTokenToRequest(request, token as string)))
      );
    }

    private addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {
      return request.clone({
        setHeaders: {
          authorization: `Bearer ${token}`
        }
      });
    }

    private handleRefreshTokenError(error?: any): Observable<never> {
      this.isRefreshing = false;
      this.refreshTokenSubject.next(null);
      this.store.dispatch(new Logout(true, this.router.url));
      return throwError(() => error || new Error('Refresh token not found'));
    }
}
